import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Tabs, Tab, CircularProgress, FormControlLabel, Checkbox } from '@mui/material';
import { X, Globe } from 'lucide-react';
import { GameCategory } from './CategoryItem';
import { siteLanguagesService, getLanguageName } from 'components/api/siteLanguages';
import { SiteLanguage } from 'types/siteLanguage';
import styles from './CreateGameCategory.module.scss';

interface CreateGameCategoryProps {
  onClose: () => void;
  onSuccess: (category: GameCategory) => void;
  initialData?: GameCategory | null;
  siteId?: string | number;
}

interface LanguageTabInfo {
  code: string;
  name: string;
  isDefault: boolean;
}

const CreateGameCategory: React.FC<CreateGameCategoryProps> = ({
  onClose,
  onSuccess,
  initialData,
  siteId,
}) => {
  const { id: paramSiteId } = useParams<{ id: string }>();
  const effectiveSiteId = siteId || paramSiteId;

  const [title, setTitle] = useState('');
  const [translations, setTranslations] = useState<Record<string, { title: string }>>({});
  const [siteLanguages, setSiteLanguages] = useState<SiteLanguage[]>([]);
  const [loadingLangs, setLoadingLangs] = useState(false);
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  const [activeTabCode, setActiveTabCode] = useState('en');

  // Sync state when initialData changes
  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || '');
      setTranslations(initialData.translations ? { ...initialData.translations } : {});
    } else {
      setTitle('');
      setTranslations({});
    }
    setActiveTabCode('en');
  }, [initialData]);

  // Fetch languages configured for the current site
  useEffect(() => {
    let isMounted = true;
    const fetchLangs = async () => {
      if (!effectiveSiteId) return;
      try {
        setLoadingLangs(true);
        const langs = await siteLanguagesService.getSiteLanguages(effectiveSiteId);
        if (isMounted && Array.isArray(langs)) {
          setSiteLanguages(langs);
        }
      } catch (err) {
        console.error('Failed to fetch site languages for category translations:', err);
      } finally {
        if (isMounted) {
          setLoadingLangs(false);
        }
      }
    };

    fetchLangs();

    return () => {
      isMounted = false;
    };
  }, [effectiveSiteId]);

  // Tab 0 is Default (EN), followed by configured site languages & any existing translation codes
  const availableTabs: LanguageTabInfo[] = useMemo(() => {
    const list: LanguageTabInfo[] = [{ code: 'en', name: 'Default (EN)', isDefault: true }];
    const seen = new Set<string>(['en']);

    siteLanguages.forEach((sl) => {
      // Filter out disabled languages if showOnlyActive is checked
      if (showOnlyActive && !sl.isEnabled) {
        return;
      }

      const code = (sl.code || '').trim().toLowerCase();
      if (code && !seen.has(code)) {
        seen.add(code);
        const langName = sl.name || getLanguageName(code) || code.toUpperCase();
        list.push({
          code,
          name: `${langName} (${code.toUpperCase()})`,
          isDefault: Boolean(sl.isDefault),
        });
      }
    });

    if (!showOnlyActive && initialData?.translations) {
      Object.keys(initialData.translations).forEach((rawCode) => {
        const code = rawCode.trim().toLowerCase();
        if (code && !seen.has(code)) {
          seen.add(code);
          const langName = getLanguageName(code) || code.toUpperCase();
          list.push({
            code,
            name: `${langName} (${code.toUpperCase()})`,
            isDefault: false,
          });
        }
      });
    }

    return list;
  }, [siteLanguages, initialData, showOnlyActive]);

  const currentTabIndex = useMemo(() => {
    const idx = availableTabs.findIndex((t) => t.code === activeTabCode);
    return idx >= 0 ? idx : 0;
  }, [availableTabs, activeTabCode]);

  const currentTab = availableTabs[currentTabIndex] || availableTabs[0];

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!title.trim()) return;

    const cleanTranslations: Record<string, { title: string }> = {};
    Object.entries(translations).forEach(([code, val]) => {
      const trimmed = val?.title?.trim();
      if (trimmed) {
        cleanTranslations[code] = { title: trimmed };
      }
    });

    const category: GameCategory = {
      id: initialData?.id || Date.now(),
      title: title.trim(),
      translations: Object.keys(cleanTranslations).length > 0 ? cleanTranslations : undefined,
      gameCount: initialData?.gameCount || 0,
      showOnFirstPage: initialData?.showOnFirstPage || false,
      image: initialData?.image,
    };
    onSuccess(category);
    onClose();
  };

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h3 className={styles.title}>
            <Globe size={20} className={styles.titleIcon} />
            {initialData ? 'Edit Game Category' : 'Create Game Category'}
          </h3>
          <p className={styles.subtitle}>
            Configure category title and language translations for this site
          </p>
        </div>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Close dialog"
        >
          <X size={20} />
        </button>
      </div>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          mt: -1,
          mb: -1.5,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={showOnlyActive}
              onChange={(e) => setShowOnlyActive(e.target.checked)}
              size="small"
              sx={{
                color: 'var(--text-secondary)',
                '&.Mui-checked': {
                  color: 'var(--accent-blue) !important',
                },
                p: 0.5,
              }}
            />
          }
          label="Show only active languages"
          sx={{
            m: 0,
            userSelect: 'none',
            '& .MuiFormControlLabel-label': {
              fontSize: '12px',
              color: 'var(--text-secondary)',
              fontWeight: 500,
              cursor: 'pointer',
            },
            '&:hover .MuiFormControlLabel-label': {
              color: 'var(--text-primary)',
            },
          }}
        />
      </Box>

      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'var(--border-color)',
          mx: -3,
          px: 3,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Tabs
          value={currentTabIndex}
          onChange={(_, newValue) => {
            if (availableTabs[newValue]) {
              setActiveTabCode(availableTabs[newValue].code);
            }
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            flex: 1,
            minHeight: '42px',
            '& .MuiTabs-scroller': {
              overflowY: 'hidden',
            },
            '& .MuiTabScrollButton-root': {
              color: 'var(--text-primary) !important',
              transition: 'color 0.2s, opacity 0.2s',
              '& svg': {
                color: 'var(--text-primary) !important',
                fill: 'currentColor',
              },
              '&.Mui-disabled': {
                opacity: 0.25,
                color: 'var(--text-secondary) !important',
                '& svg': {
                  color: 'var(--text-secondary) !important',
                },
              },
              '&:not(.Mui-disabled):hover': {
                color: 'var(--accent-blue) !important',
                '& svg': {
                  color: 'var(--accent-blue) !important',
                },
              },
            },
            '& .MuiTab-root': {
              color: 'var(--text-secondary)',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '13px',
              minHeight: '42px',
              py: 0.5,
              px: 1.5,
              minWidth: 'auto',
              transition: 'color 0.2s',
              '&:hover': {
                color: 'var(--text-primary)',
              },
            },
            '& .Mui-selected': {
              color: 'var(--accent-blue) !important',
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'var(--accent-blue)',
              height: '2px',
              borderRadius: '2px 2px 0 0',
            },
          }}
        >
          {availableTabs.map((tab) => {
            const hasValue = tab.isDefault
              ? Boolean(title.trim())
              : Boolean(translations[tab.code]?.title?.trim());

            return (
              <Tab
                key={tab.code}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <span>{tab.name}</span>
                    {hasValue && (
                      <Box
                        component="span"
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: 'var(--accent-blue)',
                          display: 'inline-block',
                        }}
                        title="Translation provided"
                      />
                    )}
                  </Box>
                }
              />
            );
          })}
        </Tabs>
        {loadingLangs && <CircularProgress size={16} sx={{ color: 'var(--accent-blue)', ml: 1 }} />}
      </Box>

      <div className={styles.inputGroup}>
        {currentTab.isDefault ? (
          <>
            <div className={styles.labelRow}>
              <label htmlFor="cat-title-default">
                Category Title (Default / English) <span className={styles.required}>*</span>
              </label>
              <span className={styles.langBadge}>EN</span>
            </div>
            <input
              id="cat-title-default"
              type="text"
              placeholder="e.g. Popular Games, Slots, Live Casino"
              className={styles.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
            <span className={styles.inputHint}>
              This title is used as default when a localized translation is not available.
            </span>
          </>
        ) : (
          <>
            <div className={styles.labelRow}>
              <label htmlFor={`cat-title-${currentTab.code}`}>
                Category Title ({currentTab.name})
              </label>
              <span className={styles.langBadge}>{currentTab.code.toUpperCase()}</span>
            </div>
            <input
              id={`cat-title-${currentTab.code}`}
              type="text"
              placeholder={`Enter localized title in ${currentTab.name}...`}
              className={styles.input}
              value={translations[currentTab.code]?.title || ''}
              onChange={(e) => {
                const val = e.target.value;
                setTranslations((prev) => ({
                  ...prev,
                  [currentTab.code]: {
                    ...prev[currentTab.code],
                    title: val,
                  },
                }));
              }}
              autoFocus
            />
            <span className={styles.inputHint}>
              Optional translation. Leave empty to use the default title for this language.
            </span>
          </>
        )}
      </div>

      <div className={styles.footer}>
        <button type="button" className={styles.cancelBtn} onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className={styles.submitBtn} disabled={!title.trim()}>
          {initialData ? 'Update Category' : 'Create Category'}
        </button>
      </div>
    </form>
  );
};

export default CreateGameCategory;
